/**
 * A 16 x 16 true-colour frame for the remastered sprites, with the few drawing moves they need:
 * shaded domes and bars, single pixels, mirroring and a selective outline. Pure (no canvas).
 */

import type { Ramp, RGB } from './color';
import { domeNormal, lambert, normalize, step } from './shade';

export const N = 16;

export class Px {
  readonly data = new Uint8ClampedArray(N * N * 4);

  set(x: number, y: number, c: RGB): void {
    if (x < 0 || y < 0 || x >= N || y >= N) return;
    const o = (y * N + x) * 4;
    this.data[o] = c[0];
    this.data[o + 1] = c[1];
    this.data[o + 2] = c[2];
    this.data[o + 3] = 255;
  }

  clear(x: number, y: number): void {
    if (x < 0 || y < 0 || x >= N || y >= N) return;
    this.data[(y * N + x) * 4 + 3] = 0;
  }

  filled(x: number, y: number): boolean {
    if (x < 0 || y < 0 || x >= N || y >= N) return false;
    return (this.data[(y * N + x) * 4 + 3] ?? 0) > 0;
  }

  /** How many pixels are painted: tests use it to prove a frame is not empty. */
  count(): number {
    let n = 0;
    for (let i = 3; i < this.data.length; i += 4) if ((this.data[i] ?? 0) > 0) n++;
    return n;
  }

  /** An ellipsoid lit from the top left, in ramp shades lo..hi; `keep` can cut it (y range etc.). */
  dome(cx: number, cy: number, rx: number, ry: number, ramp: Ramp, lo = 0, hi = 4, keep?: (x: number, y: number) => boolean): void {
    for (let y = 0; y < N; y++) {
      for (let x = 0; x < N; x++) {
        if (keep && !keep(x, y)) continue;
        const n = domeNormal(x, y, cx, cy, rx, ry);
        if (n) this.set(x, y, ramp[step(lambert(n), x, y, lo, hi)] ?? ramp[2]);
      }
    }
  }

  /** A rectangle shaded like a vertical cylinder (light from the left), inclusive corners. */
  bar(x0: number, y0: number, x1: number, y1: number, ramp: Ramp, lo = 0, hi = 4): void {
    const cx = (x0 + x1 + 1) / 2;
    const hw = Math.max(0.5, (x1 - x0 + 1) / 2);
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const nx = Math.max(-0.95, Math.min(0.95, (x + 0.5 - cx) / hw));
        const n = normalize([nx, -0.25, Math.sqrt(1 - nx * nx)]);
        this.set(x, y, ramp[step(lambert(n), x, y, lo, hi)] ?? ramp[2]);
      }
    }
  }

  /** A flat rectangle in one colour. */
  fill(x0: number, y0: number, x1: number, y1: number, c: RGB): void {
    for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) this.set(x, y, c);
  }

  /**
   * Outline in `ink` round the painted shape: every empty pixel beside a painted one. With `lit`
   * the top and left edges get `lit` instead, the pixel-art "selective outline".
   */
  outline(ink: RGB, lit?: RGB): void {
    const add: [number, number, RGB][] = [];
    for (let y = 0; y < N; y++) {
      for (let x = 0; x < N; x++) {
        if (this.filled(x, y)) continue;
        const below = this.filled(x, y + 1);
        const right = this.filled(x + 1, y);
        const above = this.filled(x, y - 1);
        const left = this.filled(x - 1, y);
        if (!below && !right && !above && !left) continue;
        add.push([x, y, lit && (below || right) && !above && !left ? lit : ink]);
      }
    }
    for (const [x, y, c] of add) this.set(x, y, c);
  }

  mirrored(): Px {
    const out = new Px();
    for (let y = 0; y < N; y++) {
      for (let x = 0; x < N; x++) {
        const src = (y * N + (N - 1 - x)) * 4;
        out.data.set(this.data.subarray(src, src + 4), (y * N + x) * 4);
      }
    }
    return out;
  }

  copy(): Px {
    const out = new Px();
    out.data.set(this.data);
    return out;
  }
}
